import { GanttTask, TimeScale, WeekStartDay } from '../types/gantt';

const MONTH_NAMES_SHORT = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
];

/**
 * Parses various date formats into standardized YYYY-MM-DD string.
 * Supports:
 * - 1-Sep-2017, 24-Sep-2017, 01-September-2017
 * - 2017-09-01, 2017/09/01
 * - 01.09.2017, 01/09/2017, 9/1/2017
 * - Sep 1, 2017
 */
export function parseFlexibleDate(input: string | number): string | null {
  if (!input) return null;
  const str = String(input).trim();
  if (!str) return null;

  // Check Excel serial number (e.g., 42979 -> 2017-09-01)
  if (/^\d{5}$/.test(str)) {
    const serial = parseInt(str, 10);
    // Excel epoch starts at Jan 1 1900 with leap year bug
    const utcDays = serial - 25569;
    const utcValue = utcDays * 86400 * 1000;
    const dateInfo = new Date(utcValue);
    if (!isNaN(dateInfo.getTime())) {
      return dateInfo.toISOString().split('T')[0];
    }
  }

  // Check ISO YYYY-MM-DD
  const isoMatch = str.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10);
    const day = parseInt(isoMatch[3], 10);
    return formatDateParts(year, month, day);
  }

  // Check DD-Mon-YYYY (e.g., 1-Sep-2017, 24-September-2017)
  const dMonYMatch = str.match(/^(\d{1,2})[-/\s]([A-Za-z]+)[-/\s,]+(\d{2,4})$/);
  if (dMonYMatch) {
    const day = parseInt(dMonYMatch[1], 10);
    const monthStr = dMonYMatch[2].toLowerCase();
    let year = parseInt(dMonYMatch[3], 10);
    if (year < 100) year += 2000;

    const month = MONTH_NAMES_SHORT.indexOf(monthStr.slice(0, 3)) + 1;
    if (month > 0) {
      return formatDateParts(year, month, day);
    }
  }

  // Check Mon DD, YYYY (e.g., Sep 1, 2017, September 1 2017)
  const monDYMatch = str.match(/^([A-Za-z]+)[-/\s]+(\d{1,2})[-/\s,]+(\d{2,4})$/);
  if (monDYMatch) {
    const monthStr = monDYMatch[1].toLowerCase();
    const day = parseInt(monDYMatch[2], 10);
    let year = parseInt(monDYMatch[3], 10);
    if (year < 100) year += 2000;

    const month = MONTH_NAMES_SHORT.indexOf(monthStr.slice(0, 3)) + 1;
    if (month > 0) {
      return formatDateParts(year, month, day);
    }
  }

  // Check DD.MM.YYYY or DD/MM/YYYY (European standard)
  const dotDateMatch = str.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/);
  if (dotDateMatch) {
    const part1 = parseInt(dotDateMatch[1], 10);
    const part2 = parseInt(dotDateMatch[2], 10);
    let year = parseInt(dotDateMatch[3], 10);
    if (year < 100) year += 2000;

    // If part1 > 12, part1 is definitely day
    if (part1 > 12) {
      return formatDateParts(year, part2, part1);
    }
    // Default to DD/MM/YYYY for dots/slashes
    return formatDateParts(year, part2, part1);
  }

  // Standard JS Date parsing fallback
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = parsed.getMonth() + 1;
    const d = parsed.getDate();
    return formatDateParts(y, m, d);
  }

  return null;
}

function formatDateParts(year: number, month: number, day: number): string {
  const m = String(month).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

export function parseDate(isoString: string): Date {
  const parts = (isoString || '').split('-').map(Number);
  if (parts.length >= 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
    return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  }
  const fallback = new Date(isoString);
  if (!isNaN(fallback.getTime())) {
    return new Date(Date.UTC(fallback.getFullYear(), fallback.getMonth(), fallback.getDate()));
  }
  return new Date();
}

export function formatDateToIso(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function daysBetween(startIso: string, endIso: string): number {
  const d1 = parseDate(startIso);
  const d2 = parseDate(endIso);
  const diffTime = d2.getTime() - d1.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
}

export function addDays(isoString: string, days: number): string {
  const date = parseDate(isoString);
  date.setUTCDate(date.getUTCDate() + days);
  return formatDateToIso(date);
}

export function formatDayMonth(d: Date | string, weekStartDay: WeekStartDay = 'monday'): string {
  const date = typeof d === 'string' ? parseDate(d) : d;
  if (weekStartDay === 'monday') {
    return formatDateToIso(date);
  }
  const day = date.getUTCDate();
  const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${month} ${day}`;
}

export interface TimelineBounds {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  totalDays: number;
  scale: 'days' | 'weeks' | 'months' | 'quarters';
  weekStartDay?: WeekStartDay;
}

export function calculateTimelineBounds(
  tasks: GanttTask[],
  preferredScale: TimeScale = 'auto',
  weekStartDay: WeekStartDay = 'monday'
): TimelineBounds {
  if (!tasks || tasks.length === 0) {
    const today = new Date();
    const start = formatDateToIso(today);
    const end = addDays(start, 30);
    return {
      startDate: start,
      endDate: end,
      totalDays: 31,
      scale: 'days',
      weekStartDay,
    };
  }

  let minTime = Infinity;
  let maxTime = -Infinity;

  tasks.forEach((t) => {
    const s = parseFlexibleDate(t.start) || t.start;
    const e = parseFlexibleDate(t.end) || t.end || s;
    if (s) {
      const timeS = parseDate(s).getTime();
      if (!isNaN(timeS)) {
        if (timeS < minTime) minTime = timeS;
        if (timeS > maxTime) maxTime = timeS;
      }
    }
    if (e) {
      const timeE = parseDate(e).getTime();
      if (!isNaN(timeE)) {
        if (timeE > maxTime) maxTime = timeE;
        if (timeE < minTime) minTime = timeE;
      }
    }
  });

  if (minTime === Infinity || maxTime === -Infinity || minTime > maxTime) {
    const now = Date.now();
    minTime = now;
    maxTime = now + 30 * 86400 * 1000;
  }

  const rawStart = new Date(minTime);
  const rawEnd = new Date(maxTime);
  const rawDays = Math.max(1, Math.round((maxTime - minTime) / (86400 * 1000)) + 1);

  let scale: 'days' | 'weeks' | 'months' | 'quarters';
  if (preferredScale && preferredScale !== 'auto') {
    scale = preferredScale;
  } else {
    // Smart auto-scale thresholds:
    // <= 35 days: show Days
    // 36 - 140 days (~4.5 months): show Weeks
    // 141 - 730 days (~2 years): show Months
    // > 730 days: show Quarters
    if (rawDays <= 35) {
      scale = 'days';
    } else if (rawDays <= 140) {
      scale = 'weeks';
    } else if (rawDays <= 730) {
      scale = 'months';
    } else {
      scale = 'quarters';
    }
  }

  // Calculate clean, aligned padding for the chosen scale
  const padStart = new Date(rawStart);
  const padEnd = new Date(rawEnd);

  if (scale === 'days') {
    // 1 day buffer on each side
    padStart.setUTCDate(padStart.getUTCDate() - 1);
    padEnd.setUTCDate(padEnd.getUTCDate() + 1);
  } else if (scale === 'weeks') {
    if (weekStartDay === 'sunday') {
      // Snap start to Sunday of that week
      const startDayOfWeek = padStart.getUTCDay(); // 0 is Sun
      padStart.setUTCDate(padStart.getUTCDate() - startDayOfWeek);

      // Snap end to Saturday of that week
      const endDayOfWeek = padEnd.getUTCDay();
      const daysForwardToSat = (6 - endDayOfWeek + 7) % 7;
      padEnd.setUTCDate(padEnd.getUTCDate() + daysForwardToSat);
    } else {
      // Snap start to Monday of that week
      const startDayOfWeek = padStart.getUTCDay(); // 0 is Sun, 1 is Mon, 6 is Sat
      const daysBackToMon = (startDayOfWeek + 6) % 7;
      padStart.setUTCDate(padStart.getUTCDate() - daysBackToMon);

      // Snap end to Sunday of that week
      const endDayOfWeek = padEnd.getUTCDay();
      const daysForwardToSun = (7 - endDayOfWeek) % 7;
      padEnd.setUTCDate(padEnd.getUTCDate() + daysForwardToSun);
    }
  } else if (scale === 'months') {
    // Snap start to 1st of month
    padStart.setUTCDate(1);
    // Snap end to last day of month
    padEnd.setUTCMonth(padEnd.getUTCMonth() + 1, 0);
  } else {
    // Quarters: Snap start to beginning of quarter (month 0, 3, 6, 9)
    const qStartMonth = Math.floor(padStart.getUTCMonth() / 3) * 3;
    padStart.setUTCMonth(qStartMonth, 1);

    // Snap end to end of quarter (month 2, 5, 8, 11)
    const qEndMonth = Math.floor(padEnd.getUTCMonth() / 3) * 3 + 3;
    padEnd.setUTCMonth(qEndMonth, 0);
  }

  const startDate = formatDateToIso(padStart);
  const endDate = formatDateToIso(padEnd);
  const totalDays = Math.max(1, daysBetween(startDate, endDate) + 1);

  return {
    startDate,
    endDate,
    totalDays,
    scale,
    weekStartDay,
  };
}

export interface HeaderColumn {
  label: string;
  subLabel?: string;
  startDayIndex: number;
  spanDays: number;
}

export interface HeaderOptions {
  showWeekNumbers?: boolean;
  weekStartDay?: WeekStartDay;
}

export function generateHeaderColumns(
  bounds: TimelineBounds,
  options: HeaderOptions = {}
): {
  primaryHeaders: HeaderColumn[];
  secondaryHeaders: HeaderColumn[];
} {
  const { startDate, totalDays, scale } = bounds;
  const showWeekNumbers = options.showWeekNumbers !== false;
  const weekStartDay = options.weekStartDay || bounds.weekStartDay || 'monday';

  const primaryHeaders: HeaderColumn[] = [];
  const secondaryHeaders: HeaderColumn[] = [];

  const start = parseDate(startDate);

  if (scale === 'days') {
    let currentMonthKey = '';
    let monthStart = 0;
    let monthLabel = '';

    for (let i = 0; i < totalDays; i++) {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + i);

      const mKey = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
      const monthName = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
      const dayName = d.toLocaleDateString('en-US', { weekday: 'narrow', timeZone: 'UTC' });
      const dayNum = d.getUTCDate();

      if (mKey !== currentMonthKey) {
        if (currentMonthKey !== '') {
          primaryHeaders.push({
            label: monthLabel,
            startDayIndex: monthStart,
            spanDays: i - monthStart,
          });
        }
        currentMonthKey = mKey;
        monthStart = i;
        monthLabel = monthName;
      }

      secondaryHeaders.push({
        label: `${dayNum}`,
        subLabel: dayName,
        startDayIndex: i,
        spanDays: 1,
      });
    }

    if (monthStart < totalDays) {
      primaryHeaders.push({
        label: monthLabel,
        startDayIndex: monthStart,
        spanDays: totalDays - monthStart,
      });
    }
  } else if (scale === 'weeks') {
    let currentMonthKey = '';
    let monthStart = 0;
    let monthLabel = '';

    for (let i = 0; i < totalDays; i += 7) {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + i);
      const span = Math.min(7, totalDays - i);

      const mKey = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
      const monthName = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
      const weekNum = getWeekNumber(d, weekStartDay);
      const startDayStr = `${d.getUTCDate()}`;

      if (mKey !== currentMonthKey) {
        if (currentMonthKey !== '') {
          primaryHeaders.push({
            label: monthLabel,
            startDayIndex: monthStart,
            spanDays: i - monthStart,
          });
        }
        currentMonthKey = mKey;
        monthStart = i;
        monthLabel = monthName;
      }

      if (showWeekNumbers) {
        secondaryHeaders.push({
          label: `W${weekNum}`,
          subLabel: startDayStr,
          startDayIndex: i,
          spanDays: span,
        });
      } else {
        const startFormatted = formatDayMonth(d, weekStartDay);
        const weekdayShort = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });

        secondaryHeaders.push({
          label: startFormatted,
          subLabel: weekdayShort,
          startDayIndex: i,
          spanDays: span,
        });
      }
    }

    if (monthStart < totalDays) {
      primaryHeaders.push({
        label: monthLabel,
        startDayIndex: monthStart,
        spanDays: totalDays - monthStart,
      });
    }
  } else if (scale === 'months') {
    let currentYear = -1;
    let yearStart = 0;
    let yearLabel = '';

    let i = 0;
    while (i < totalDays) {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + i);

      const y = d.getUTCFullYear();
      const m = d.getUTCMonth();
      const monthName = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });

      // Calculate days in this month
      const nextMonth = new Date(Date.UTC(y, m + 1, 1));
      const daysInThisMonthChunk = Math.min(
        totalDays - i,
        Math.round((nextMonth.getTime() - d.getTime()) / (86400 * 1000))
      );

      if (y !== currentYear) {
        if (currentYear !== -1) {
          primaryHeaders.push({
            label: yearLabel,
            startDayIndex: yearStart,
            spanDays: i - yearStart,
          });
        }
        currentYear = y;
        yearStart = i;
        yearLabel = `${y}`;
      }

      secondaryHeaders.push({
        label: monthName,
        startDayIndex: i,
        spanDays: Math.max(1, daysInThisMonthChunk),
      });

      i += Math.max(1, daysInThisMonthChunk);
    }

    if (yearStart < totalDays) {
      primaryHeaders.push({
        label: yearLabel,
        startDayIndex: yearStart,
        spanDays: totalDays - yearStart,
      });
    }
  } else {
    // Quarters
    let i = 0;
    let currentYear = -1;
    let yearStart = 0;
    let yearLabel = '';

    while (i < totalDays) {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + i);

      const y = d.getUTCFullYear();
      const m = d.getUTCMonth();
      const q = Math.floor(m / 3) + 1;

      // End of this quarter
      const nextQuarterMonth = q * 3;
      const nextQuarterDate = new Date(Date.UTC(y, nextQuarterMonth, 1));
      const daysInChunk = Math.min(
        totalDays - i,
        Math.round((nextQuarterDate.getTime() - d.getTime()) / (86400 * 1000))
      );

      if (y !== currentYear) {
        if (currentYear !== -1) {
          primaryHeaders.push({
            label: yearLabel,
            startDayIndex: yearStart,
            spanDays: i - yearStart,
          });
        }
        currentYear = y;
        yearStart = i;
        yearLabel = `${y}`;
      }

      secondaryHeaders.push({
        label: `Q${q}`,
        startDayIndex: i,
        spanDays: Math.max(1, daysInChunk),
      });

      i += Math.max(1, daysInChunk);
    }

    if (yearStart < totalDays) {
      primaryHeaders.push({
        label: yearLabel,
        startDayIndex: yearStart,
        spanDays: totalDays - yearStart,
      });
    }
  }

  return { primaryHeaders, secondaryHeaders };
}

export function getWeekNumber(d: Date, weekStartDay: WeekStartDay = 'monday'): number {
  if (weekStartDay === 'sunday') {
    // US / Sunday-based week calculation: Week 1 contains Jan 1
    const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const jan1 = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
    const jan1DayOffset = jan1.getUTCDay(); // 0 is Sun
    const diffDays = Math.floor((target.getTime() - jan1.getTime()) / (24 * 60 * 60 * 1000));
    return Math.floor((diffDays + jan1DayOffset) / 7) + 1;
  }

  // ISO-8601 (Monday-based)
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = target.getTime();
  target.setUTCMonth(0, 1);
  if (target.getUTCDay() !== 4) {
    target.setUTCMonth(0, 1 + ((4 - target.getUTCDay() + 7) % 7));
  }
  return 1 + Math.ceil((firstThursday - target.getTime()) / 604800000);
}
